import { useState } from 'preact/hooks';
import type { ChainRef, LogClient } from '../../core/log/client';
import { parseCertificate, type ParsedCert } from '../../core/x509/parse';
import { useAsync } from '../hooks/useAsync';
import { Badge } from './Badge';
import { CertSummary } from './CertSummary';
import { ErrorBox } from './ErrorBox';
import { ExtensionList } from './ExtensionList';
import { Hash } from './Hash';
import { Icon } from './Icon';
import { Spinner } from './Spinner';
import { ExportMenu } from './Export';
import { pemEncode } from '../../core/bytes/encoding';

/** Issuer chain: RFC 6962 logs give DER; tiled logs give fingerprints resolved via /issuer/. */
export function ChainView({ chain, client, filePrefix }: { chain: ChainRef[]; client: LogClient; filePrefix: string }) {
  if (chain.length === 0) return <p class="muted">The log did not return an issuer chain for this entry.</p>;
  return (
    <ol class="chain">
      {chain.map((ref, i) => (
        <li key={i} class="chain-item">
          <ChainItem item={ref} client={client} position={i} filePrefix={filePrefix} />
        </li>
      ))}
    </ol>
  );
}

function ChainItem({ item: ref, client, position, filePrefix }: { item: ChainRef; client: LogClient; position: number; filePrefix: string }) {
  const state = useAsync(
    async (signal) => {
      if ('der' in ref) return ref.der;
      if (!client.resolveIssuer) throw new Error('This log cannot resolve issuers by fingerprint');
      return client.resolveIssuer(ref.fingerprint, signal);
    },
    [ref, client],
  );
  const [open, setOpen] = useState(false);
  if (state.status === 'error') return <div><ErrorBox error={state.error} title="Could not load issuer certificate" /></div>;
  if (!state.value) return <div><Spinner label={'fingerprint' in ref ? `Resolving issuer ${ref.fingerprint.toHex().slice(0, 12)}…` : 'Loading…'} /></div>;
  let parsed: ParsedCert | undefined;
  let parseError: string | undefined;
  try {
    parsed = parseCertificate(state.value);
  } catch (e) {
    parseError = e instanceof Error ? e.message : String(e);
  }
  const der = state.value;
  return (
    <div>
      <div class="row">
        <strong>{parsed?.commonName ?? '(unparseable)'}</strong>
        {parsed?.isPrecertSigningCert && <Badge tone="info">precert signing</Badge>}
        {parsed && parsed.subject === parsed.issuer && <Badge tone="outline">self-signed</Badge>}
        {parsed?.isCA && !parsed.isPrecertSigningCert && <Badge tone="outline">CA</Badge>}
        <span class="muted tiny">{der.length} B</span>
        <span class="right row">
          <button type="button" class="btn btn-sm btn-ghost" onClick={() => setOpen((v) => !v)}>{open ? 'Hide details' : 'Details'}</button>
          <ExportMenu ghost label="Export" formats={[{ label: 'PEM', filename: `${filePrefix}-chain${position}.pem`, mime: 'application/x-pem-file', text: () => pemEncode(der) }, { label: 'DER', filename: `${filePrefix}-chain${position}.der`, mime: 'application/pkix-cert', bytes: der }]} />
        </span>
      </div>
      {parsed && <div class="tiny muted mono wrap">{parsed.subject}</div>}
      {'fingerprint' in ref && <div class="tiny row" style="margin-top:0.2rem">fingerprint <Hash value={ref.fingerprint} short /></div>}
      {parseError && <div class="small muted">{parseError}</div>}
      {open && parsed && (
        <div class="chain-details">
          <CertSummary cert={parsed} />
          <h3 style="margin:0.9rem 0 0.4rem">Extensions</h3>
          <ExtensionList extensions={parsed.extensions} />
        </div>
      )}
    </div>
  );
}
